import {
  Component,
  OnInit,
  ViewChild,
  ElementRef,
  OnDestroy,
  ChangeDetectorRef,
  HostListener
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Application } from '@enums/application.enums';
import { CommonService } from '@modules/shared/services/common.service';
import { MinimizedPlayerService } from '@modules/shared/services/minimized-player.service';
import { MediaService } from '@modules/shared/services/media.service';
import { AuthService } from '@core/auth.service';
import { BsModalService } from 'ngx-bootstrap/modal';
import { MusicPlayerModalComponent } from '@components/player-modal/music-player-modal/music-player-modal.component';
import cloneDeep from 'lodash/cloneDeep';
import { DisableRightClickDirective } from '@modules/shared/directive/disable-right-click.directive';
import { MobileOptimizationDirective } from '@app-shared/directives/mobile-optimization.directive';
import { API_URLS } from '@core/api-url.enum';
import { ApiCallService } from '@services/api-call.service';
import { PortfolioRecordPlayService } from '@services/portfolio-record-play.service';
import {
  MUSIC_VOLUME_STORAGE_KEY,
  GET_VOLUME_FROM_STORAGE
} from '@components/player-modal/constant';
import { generateRandomId } from '@utils/common.utils';
import { CONVERSION_MULTIPLIERS } from '@constants/constant';

@Component({
  selector: 'app-minimized-player',
  templateUrl: './minimized-player.component.html',
  styleUrls: ['./minimized-player.component.scss'],
  standalone: true,
  imports: [CommonModule, DisableRightClickDirective, MobileOptimizationDirective]
})
export class MinimizedPlayerComponent implements OnInit, OnDestroy {
  @ViewChild('audioPlayer', { static: false })
  audioPlayer: ElementRef<HTMLAudioElement>;

  currentMusic: any = null;
  musicList: any[] = [];
  currentIndex: number = Application.ZERO;
  isPlaying: boolean = false;
  currentTime: number = Application.ZERO;
  duration: number = Application.ZERO;
  volume: number = Application.ONE;
  isMuted: boolean = false;
  playbackRate: number = Application.ONE;
  playbackRates: number[] = [0.5, 0.75, 1, 1.25, 1.5, 2];
  showSpeedMenu: boolean = false;
  showVolumeSlider: boolean = false;
  isLocked: boolean = false;
  isMobile: boolean = false;
  isLoadingAudio: boolean = false;
  isMinimized: boolean = false;
  initialState: any = null;
  isMyProfile: boolean = false;

  private audioElement: HTMLAudioElement | null = null;
  private rafId: number | null = null;
  private destroy$ = new Subject<void>();
  private resizeListener?: () => void;
  private isInitialized: boolean = false;
  private lastStateHash: string = '';
  private lastTimeUpdate: number = Application.ZERO;
  lockedThrough: any = null;
  Application = Application;

  private playSessionId: string | null = null;
  private playLastRecordedTime: number = Application.ZERO;

  constructor(
    public readonly cs: CommonService,
    private readonly minimizedPlayerService: MinimizedPlayerService,
    private readonly mediaService: MediaService,
    public readonly authService: AuthService,
    private readonly modalService: BsModalService,
    private readonly cdr: ChangeDetectorRef,
    private readonly apiCallService: ApiCallService,
    private readonly portfolioRecordPlayService: PortfolioRecordPlayService
  ) {}

  ngOnInit() {
    if (!this.cs.isBrowser) return;

    this.isMobile = window.innerWidth <= Application.SEVEN_SIXTY_EIGHT;
    this.resizeListener = () => {
      this.isMobile = window.innerWidth <= Application.SEVEN_SIXTY_EIGHT;
      this.cdr.detectChanges();
    };
    window.addEventListener('resize', this.resizeListener);

    // Load saved volume from localStorage
    this.loadSavedVolume();

    // Subscribe to minimized player state
    this.minimizedPlayerService.state$
      .pipe(takeUntil(this.destroy$))
      .subscribe((state) => {
        if (state.isMinimized && state.currentMusic) {
          // Create a hash to detect if this is a new song or just a state update
          const stateHash = `${state.currentMusic?.audioUrl || ''}-${state.currentIndex}`;
          const isNewSong = this.lastStateHash !== stateHash;

          this.isMinimized = true;
          this.currentMusic = state.currentMusic;
          this.musicList = state.musicList;
          this.currentIndex = state.currentIndex;
          this.isPlaying = state.isPlaying;
          this.currentTime = state.currentTime;
          this.duration = state.duration;
          this.initialState = state.initialState;
          this.isMyProfile = state.initialState?.isMyProfile || false;

          if (state.playSessionId != null) {
            this.playSessionId = state.playSessionId;
          }
          if (state.playLastRecordedTime != null) {
            this.playLastRecordedTime = state.playLastRecordedTime;
          }

          // Update locked state
          this.isLocked = this.currentMusic?.isLocked || false;

          // Add body class when minimized
          if (this.cs.isBrowser) {
            document.body.classList.add('music-player-minimized');
          }

          // Only initialize audio element if it's a new song or not yet initialized
          if (state.audioElement && (isNewSong || !this.isInitialized)) {
            this.lastStateHash = stateHash;
            this.isInitialized = false; // Reset to allow re-initialization for new song

            // Use setTimeout to ensure audio element is available in the DOM
            setTimeout(() => {
              if (this.audioPlayer?.nativeElement && !this.isInitialized) {
                this.audioElement = this.audioPlayer.nativeElement;
                this.syncAudioElement(state.audioElement);
                this.isInitialized = true;

                // Resume playback if it was playing (after a brief delay to ensure audio is loaded)
                if (state.isPlaying) {
                  setTimeout(() => {
                    if (this.audioElement && !this.audioElement.paused) {
                      // Already playing, do nothing
                    } else if (this.audioElement) {
                      this.audioElement.play().catch(() => {});
                    }
                  }, 100);
                }
              }
            }, 50);
          } else if (this.audioElement && state.audioElement) {
            // Just sync time/state without re-initializing for regular updates
            // Only update if it's the same song (not a new song)
            if (
              this.currentMusic?.audioUrl === state.currentMusic?.audioUrl &&
              this.currentIndex === state.currentIndex
            ) {
              this.currentTime = state.currentTime;
              this.isPlaying = state.isPlaying;
              if (
                Math.abs(this.audioElement.currentTime - state.currentTime) >
                0.5
              ) {
                this.audioElement.currentTime = state.currentTime;
              }
            } else {
              // New song detected - reset time to 0
              this.currentTime = Application.ZERO;
              if (this.audioElement) {
                this.audioElement.currentTime = Application.ZERO;
              }
            }
          }

          this.cdr.detectChanges();
        } else {
          this.isMinimized = false;
          this.currentMusic = null;
          this.isInitialized = false;
          this.lastStateHash = '';
          this.playSessionId = null;
          this.playLastRecordedTime = Application.ZERO;

          // Remove body class when not minimized
          if (this.cs.isBrowser) {
            document.body.classList.remove('music-player-minimized');
          }

          this.cdr.detectChanges();
        }
      });

    // Subscribe to current song updates
    this.minimizedPlayerService.updateCurrentSongSubject
      .pipe(takeUntil(this.destroy$))
      .subscribe((state: any) => {
        if (state) {
          this.currentMusic = state.currentMusic;
          this.musicList = state.musicList;
          this.currentIndex = state.currentIndex;
          this.isPlaying = state.isPlaying;
          this.currentTime = state.currentTime;
          this.duration = state.duration;
          this.loadCurrentMusic();
        }
      });

    // Subscribe to video unmute events - pause music player when video is unmuted
    this.mediaService.videoUnmuted$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.isMinimized && this.isPlaying && this.audioElement) {
          this.audioElement.pause();
        }
        this.mediaService.pauseAllNgxAudioPlayers();
      });
  }

  ngAfterViewInit() {
    if (!this.cs.isBrowser) return;
    setTimeout(() => {
      if (this.audioPlayer?.nativeElement) {
        this.audioElement = this.audioPlayer.nativeElement;
        const state = this.minimizedPlayerService.getState();
        if (state.isMinimized && state.audioElement && state.audioElement.src) {
          this.syncAudioElement(state.audioElement);
          // Only auto-play if it was playing and not locked
          if (state.isPlaying && !this.isLocked) {
            this.audioElement.play().catch(() => {});
          }
        }
      }
    }, 100);
  }

  private get isEngagable(): boolean {
    return (
      this.currentMusic?.pk &&
      this.currentMusic?.sk &&
      !this.isMyProfile
    )
  }

  private syncAudioElement(sourceElement: HTMLAudioElement) {
    if (!this.audioElement || !sourceElement) return;

    // Copy all audio properties from source to minimized player's audio element
    this.audioElement.src = sourceElement.src;
    this.audioElement.currentTime = sourceElement.currentTime;
    this.audioElement.volume = sourceElement.volume;
    this.audioElement.muted = sourceElement.muted;
    this.audioElement.playbackRate = sourceElement.playbackRate;

    // Update component state
    this.volume = sourceElement.volume;
    this.isMuted = sourceElement.muted;
    this.playbackRate = sourceElement.playbackRate;
    this.currentTime = sourceElement.currentTime;

    // Load the audio to ensure it's ready
    this.audioElement.load();
  }

  onAudioLoadedMetadata() {
    if (this.audioElement) {
      const newDuration = this.audioElement.duration;
      if (newDuration && !isNaN(newDuration) && isFinite(newDuration)) {
        this.duration = newDuration;
        this.minimizedPlayerService.updateState({ duration: this.duration });
      }
      this.isLoadingAudio = false;
    }
  }

  onAudioLoadStart() {
    this.isLoadingAudio = true;
  }

  onAudioError() {
    this.isLoadingAudio = false;
  }

  onAudioTimeUpdate() {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
    }
    this.rafId = requestAnimationFrame(() => {
      if (this.audioElement) {
        this.currentTime = this.audioElement.currentTime;
        // Only update state every 0.5 seconds to reduce subscription triggers
        // Store the last update time
        const now = Date.now();
        if (!this.lastTimeUpdate || now - this.lastTimeUpdate > 500) {
          this.minimizedPlayerService.updateState({
            currentTime: this.currentTime,
            isPlaying: this.isPlaying,
            audioElement: this.audioElement
          });
          this.lastTimeUpdate = now;
        }
      }
      this.rafId = null;
    });
  }

  onAudioPlay() {
    this.ensurePlaySession();
    this.isPlaying = true;
    this.minimizedPlayerService.updateState({
      isPlaying: true,
      audioElement: this.audioElement,
      playSessionId: this.playSessionId ?? undefined,
      playLastRecordedTime: this.playLastRecordedTime
    });
    // Notify that music player started - this will mute any unmuted videos
    this.mediaService.notifyMusicPlayerStarted();
    this.mediaService.muteAllUnmutedVideos();
    this.mediaService.pauseAllNgxAudioPlayers();
  }

  onAudioPause() {
    if (this.audioElement && this.playSessionId) {
      const currentTimeSec = this.audioElement.currentTime;
      this.sendRecordPlay(currentTimeSec);
      this.minimizedPlayerService.updateState({
        playSessionId: this.playSessionId ?? undefined,
        playLastRecordedTime: this.playLastRecordedTime
      });
    }
    this.isPlaying = false;
    this.minimizedPlayerService.updateState({
      isPlaying: false,
      audioElement: this.audioElement
    });
  }

  onAudioEnded() {
    if (this.audioElement && this.playSessionId) {
      const currentTimeSec = this.audioElement.currentTime;
      this.sendRecordPlay(currentTimeSec);
    }
    if (this.hasNextMusic() && !this.isLocked) {
      this.playNextMusic();
    }
  }

  onAudioVolumeChange() {
    if (this.audioElement) {
      this.volume = this.audioElement.volume;
      this.isMuted = this.audioElement.muted;
    }
  }

  onAudioRateChange() {
    if (this.audioElement) {
      this.playbackRate = this.audioElement.playbackRate;
    }
  }

  togglePlayPause() {
    if (this.isLocked && !this.currentMusic?.canPreview) return;
    if (this.audioElement) {
      if (this.isPlaying) {
        this.audioElement.pause();
      } else {
        this.audioElement.play();
      }
    }
  }

  rewind15() {
    if (this.audioElement) {
      const newTime = Math.max(
        Application.ZERO,
        this.currentTime - Application.FIFTEEN
      );
      this.audioElement.currentTime = newTime;
    }
  }

  fastForward15() {
    if (this.audioElement) {
      const newTime = Math.min(
        this.duration,
        this.currentTime + Application.FIFTEEN
      );
      this.audioElement.currentTime = newTime;
    }
  }

  playPreviousMusic() {
    if (this.hasPreviousMusic()) {
      this.sendRecordPlayForCurrentTrack();
      this.currentIndex--;
      this.currentMusic = null;
      this.isInitialized = false;
      this.lastStateHash = '';
      setTimeout(() => {
        this.loadCurrentMusic();
      }, 10);
    }
  }

  playNextMusic() {
    if (this.hasNextMusic()) {
      this.sendRecordPlayForCurrentTrack();
      this.currentIndex++;
      this.currentMusic = null;
      this.isInitialized = false;
      this.lastStateHash = '';
      setTimeout(() => {
        this.loadCurrentMusic();
      }, 10);
    }
  }

  hasPreviousMusic(): boolean {
    return (
      this.musicList.length > Application.ZERO &&
      this.currentIndex > Application.ZERO
    );
  }

  hasNextMusic(): boolean {
    return (
      this.musicList.length > Application.ZERO &&
      this.currentIndex < this.musicList.length - Application.ONE
    );
  }

  loadCurrentMusic() {
    if (this.musicList.length === Application.ZERO) return;

    const currentMusic = this.musicList[this.currentIndex];
    if (currentMusic) {
      this.playSessionId = null;
      this.playLastRecordedTime = Application.ZERO;

      if (this.audioElement) {
        this.audioElement.pause();
        this.audioElement.currentTime = Application.ZERO;
        this.currentTime = Application.ZERO;
        this.duration = Application.ZERO;
        this.isPlaying = false;
      }

      this.isLocked = currentMusic?.isLocked || false;
      this.lockedThrough = currentMusic.lock_through || null;
      this.currentMusic = cloneDeep(currentMusic);
      this.callEngagementAPI();

      // Update service state with new current music (reset time to 0 for new song)
      this.minimizedPlayerService.updateState({
        currentMusic: this.currentMusic,
        currentIndex: this.currentIndex,
        isLocked: this.isLocked,
        currentTime: Application.ZERO as number,
        isPlaying: false
      });

      if (!this.isLocked || this.currentMusic?.canPreview) {
        this.isLoadingAudio = true;
        setTimeout(() => {
          if (this.audioPlayer?.nativeElement) {
            this.audioElement = this.audioPlayer.nativeElement;
            // Always set new source and reset
            this.audioElement.src = currentMusic.audioUrl;
            this.audioElement.load();
            this.setupAudioElement();
          }
        }, 100);
      }
    }
  }

  private callEngagementAPI(): void {
    if (!this.isEngagable || !this.authService.isLogin) return;

    const payload = {
      pk: this.currentMusic.pk,
      sk: this.currentMusic.sk
    };

    this.apiCallService
      .post(API_URLS.portfolioEngagement, payload, false)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {},
        error: () => {}
      });
  }

  private ensurePlaySession(): void {
    if (!this.playSessionId) {
      this.playSessionId = `session_${generateRandomId()}`;
      this.playLastRecordedTime = Application.ZERO;
    }
  }

  private sendRecordPlay(
    currentTimeSec: number,
    updateLastRecordedTo?: number
  ): void {
    if (!this.isEngagable || !this.playSessionId) return;

    const durationPlayedMs = Math.max(
      Application.ZERO,
      (currentTimeSec - this.playLastRecordedTime) * CONVERSION_MULTIPLIERS.SECONDS_TO_MILLISECONDS
    );
    this.playLastRecordedTime =
      updateLastRecordedTo !== undefined
        ? updateLastRecordedTo
        : currentTimeSec;
    this.portfolioRecordPlayService.recordPlay({
      pk: this.currentMusic.pk,
      sk: this.currentMusic.sk,
      duration_played: durationPlayedMs,
      session_id: this.playSessionId
    });
  }

  private sendRecordPlayForCurrentTrack(): void {
    const currentTimeSec =
      this.audioElement?.currentTime ?? this.currentTime ?? Application.ZERO;
    if (this.playSessionId) {
      this.sendRecordPlay(currentTimeSec);
    }
  }

  setupAudioElement() {
    if (!this.audioElement || !this.cs.isBrowser) return;

    this.audioElement.currentTime = Application.ZERO;
    this.audioElement.muted = false;
    // Use saved volume instead of always resetting to 1
    this.audioElement.volume = this.volume;
    this.audioElement.playbackRate = Application.ONE;

    this.audioElement.load();

    if (
      this.audioElement.duration &&
      !isNaN(this.audioElement.duration) &&
      isFinite(this.audioElement.duration)
    ) {
      this.duration = this.audioElement.duration;
    }

    if ('mediaSession' in navigator) {
      navigator.mediaSession.metadata = new MediaMetadata({
        title: this.currentMusic?.songTitle || '',
        artist: '',
        album: this.currentMusic?.albumTitle || '',
        artwork: [
          {
            src: this.currentMusic?.previewImageUrl,
            sizes: '96x96',
            type: 'image/webp'
          }
        ]
      });
    }
    this.audioElement.play().catch(() => {});
  }

  toggleMute() {
    if (this.audioElement) {
      this.audioElement.muted = !this.isMuted;
    }
  }

  toggleVolumeSlider() {
    this.showVolumeSlider = !this.showVolumeSlider;
    if (this.showSpeedMenu) {
      this.showSpeedMenu = false;
    }
  }

  onVolumeChange(event: Event) {
    const target = event.target as HTMLInputElement;
    const newVolume = parseFloat(target.value);
    this.updateVolume(newVolume);
  }

  private updateVolume(newVolume: number) {
    if (this.audioElement) {
      this.audioElement.volume = newVolume;
      this.volume = newVolume;
      if (newVolume > Application.ZERO && this.isMuted) {
        this.audioElement.muted = false;
        this.isMuted = false;
      }
      this.saveVolume(newVolume);
    }
  }

  private loadSavedVolume() {
    if (this.cs.isBrowser) {
      this.volume = GET_VOLUME_FROM_STORAGE();
    }
  }

  private saveVolume(volume: number) {
    if (this.cs.isBrowser) {
      try {
        localStorage.setItem(MUSIC_VOLUME_STORAGE_KEY, volume.toString());
      } catch (error) {
        // If localStorage is not available, silently fail
      }
    }
  }

  setPlaybackRate(rate: number) {
    if (this.audioElement) {
      this.audioElement.playbackRate = rate;
      this.showSpeedMenu = false;
    }
  }

  toggleSpeedMenu() {
    this.showSpeedMenu = !this.showSpeedMenu;
    if (this.showVolumeSlider) {
      this.showVolumeSlider = false;
    }
  }

  seek(event: MouseEvent) {
    if (this.isLocked && !this.currentMusic?.canPreview) return;
    if (this.audioElement && event.target) {
      const progressBar = event.currentTarget as HTMLElement;
      const rect = progressBar.getBoundingClientRect();
      const percent = (event.clientX - rect.left) / rect.width;
      const newTime = percent * this.duration;
      const currentTimeBefore = this.audioElement.currentTime;
      if (
        this.playSessionId &&
        (currentTimeBefore - this.playLastRecordedTime) * CONVERSION_MULTIPLIERS.SECONDS_TO_MILLISECONDS > Application.ZERO
      ) {
        this.sendRecordPlay(currentTimeBefore, newTime);
      } else {
        this.playLastRecordedTime = newTime;
      }
      this.audioElement.currentTime = newTime;
    }
  }

  formatTime(seconds: number): string {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  getProgressPercent(): number {
    if (!this.duration) return 0;
    return (this.currentTime / this.duration) * 100;
  }

  maximizeModal() {
    if (!this.currentMusic || !this.initialState) return;

    // Pause audio in minimized player
    if (this.audioElement) {
      const wasPlaying = this.isPlaying;
      this.audioElement.pause();

      // Restore modal with current state
      const modalConfig = {
        initialState: {
          ...this.initialState,
          musicList: this.musicList,
          currentIndex: this.currentIndex,
          isLocked: this.isLocked
        }
      };

      // Close minimized player first
      this.minimizedPlayerService.maximize();

      // Open modal
      const modalRef = this.modalService.show(MusicPlayerModalComponent, {
        ...modalConfig,
        class: 'modal-lg',
        backdrop: true,
        keyboard: true
      });

      // Restore playback state after modal opens
      if (wasPlaying && modalRef.content) {
        setTimeout(() => {
          if (modalRef.content?.audioElement) {
            modalRef.content.audioElement.currentTime = this.currentTime;
            modalRef.content.audioElement.volume = this.volume;
            modalRef.content.audioElement.muted = this.isMuted;
            modalRef.content.audioElement.playbackRate = this.playbackRate;
            if (wasPlaying) {
              modalRef.content.audioElement.play().catch(() => {});
            }
          }
        }, 200);
      }
    }
  }

  closePlayer() {
    const currentTimeSec =
      this.audioElement?.currentTime ?? this.currentTime ?? Application.ZERO;
    if (this.playSessionId) {
      this.sendRecordPlay(currentTimeSec);
    }
    if (this.audioElement) {
      this.audioElement.pause();
      this.audioElement.currentTime = Application.ZERO;
    }

    // Remove body class when closing player
    if (this.cs.isBrowser) {
      document.body.classList.remove('music-player-minimized');
    }

    this.minimizedPlayerService.clear();
  }

  @HostListener('document:click', ['$event'])
  handleDocumentClick(event: MouseEvent) {
    if (this.showVolumeSlider && this.cs.isBrowser) {
      const target = event.target as HTMLElement;
      const volumeControl = target.closest('.volume-control');
      if (!volumeControl) {
        this.showVolumeSlider = false;
      }
    }
    if (this.showSpeedMenu && this.cs.isBrowser) {
      const target = event.target as HTMLElement;
      const speedControl = target.closest('.speed-control');
      if (!speedControl) {
        this.showSpeedMenu = false;
      }
    }
  }

  ngOnDestroy() {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }

    if (this.resizeListener) {
      window.removeEventListener('resize', this.resizeListener);
    }

    // Remove body class on destroy
    if (this.cs.isBrowser) {
      document.body.classList.remove('music-player-minimized');
    }

    this.destroy$.next();
    this.destroy$.complete();
  }
}
